/**
 * THE STANDING ACCOUNT: what the sheet says about the player when nothing has just happened.
 *
 * -- WHY THIS FILE EXISTS, MEASURED RATHER THAN ASSUMED -------------------------
 *
 * The owner looked at the running game and said "Currently seeing no GUI". A round was spent
 * answering that inside the ledger - three careful additions, all of them behind a key the
 * game named nowhere a player could read - and the owner looked again and said "there still
 * is a lack of GUI". The measurement behind the second verdict: `world.html` named the
 * controls in one `<span class="hint">` inside a `#strip` that is `display: none` unless
 * `body.dev`, so a normal player was told nothing, did not know `L` existed, and therefore
 * could not reach a single thing built inside the ledger. Level, experience, coins and the
 * personal best were computed by the engine and rendered nowhere at all.
 *
 * So the account is written on the surface that is already always on screen, and it is
 * written rather than racked. Every line here obeys `chart-copy.ts`'s three rules, which are
 * the reason this reads as a fishing log and not as a status bar:
 *
 *   IT IS A PHRASE, NOT A FIELD. "Level 41, 4,180 more to 42" and never "LVL 41 XP 4180".
 *   The distance between a HUD and a dashboard is not how much it says. It is whether the
 *   thing saying it is a sentence or a colon.
 *   IT SPEAKS IN THE INTERFACE'S VOICE. Sentence case, one clause a fact, no exclamation.
 *   IT NAMES NOTHING THE PLAYER HAS NOT FOUND. An empty tin is silent rather than "0 coins",
 *   and the sentence that says the ledger exists is gone the moment they have opened it.
 *
 * -- AND WHY THE FIGURES ARE ROUNDED -------------------------------------------
 *
 * A figure written exactly changes on every catch, and the host repaints the sheet whenever a
 * pixel of it would differ, so an exact coin count makes the paper a thing that is redrawn
 * several times a second forever. Rounding is not a cosmetic choice here, it is the whole of
 * what keeps a standing account cheap: `countWords` moves at most once per hundred coins
 * above ten thousand and once per hundred thousand above a million, so the sheet at rest is
 * still a sheet at rest. It is also what a ledger does. Nobody writes 4,582,651 in a log.
 */

import { MAX_LEVEL, levelForXp, speciesById, variantById, xpToNextLevel, type SimState } from '../sim/index';
import { countWords, weightWords } from './book';

/**
 * WHETHER THE FOOT STILL HAS TO SAY THAT THE LEDGER IS THERE.
 *
 *   'choose'  the player has never opened it AND has never set a target, so the sentence
 *             says what the ledger is for as well as which key opens it. This is the first
 *             thing a new save reads and it is the only tutorial in the game.
 *   'ledger'  they have a target but have not opened the book this session.
 *   'none'    they have opened it. The sentence never comes back, because an instruction
 *             that outlives its usefulness is the permanent chrome this sheet refuses.
 */
export type LedgerHint = 'choose' | 'ledger' | 'none';

const HINT: Readonly<Record<LedgerHint, string>> = {
  choose: 'L opens the ledger: choose what to fish for.',
  ledger: 'L opens the ledger.',
  none: '',
};

/**
 * THE PAGE'S OWN KEY, written at the foot while the ledger is lifted.
 *
 * The mastery measure is an ink run along each row's rule and nothing on the sheet said so.
 * An open finding records that as one of three marks sharing a visual family; the heading
 * gave up its underline for the blot in the same change, which leaves this the only
 * horizontal ink run on the page and therefore the only thing this sentence can mean.
 */
export const MASTERY_KEY = 'Ink under a name is the hours in it.';

/** "An opah" but "A cod". The face carries no small caps, so the article does the work. */
function article(word: string): string {
  return /^[aeiou]/i.test(word) ? 'An' : 'A';
}

/**
 * THE STANDING ACCOUNT, or the ledger's own key while the book is open.
 *
 * Two short sentences and never a third fact crammed into one: the level and what is owed to
 * the next, then the tin. An empty tin is not written, so a save that has landed nothing says
 * one sentence and the invitation, which is the whole of a new player's first impression.
 */
export function accountLine(state: SimState, hint: LedgerHint): string {
  const level = levelForXp(state.xp);
  const owed = xpToNextLevel(state.xp);
  const parts: string[] = [];
  parts.push(level >= MAX_LEVEL || owed <= 0 ? `Level ${level}.` : `Level ${level}, ${countWords(owed)} more to ${level + 1}.`);
  if (state.coins > 0) parts.push(`${countWords(state.coins)} coin${state.coins === 1 ? '' : 's'}.`);
  const say = HINT[hint];
  if (say.length > 0) parts.push(say);
  return parts.join(' ');
}

/**
 * THE HEAVIEST ONE OF THIS SPECIES EVER LANDED, for the pole under its count.
 *
 * Empty when there is no record for it, which is the same refusal `bookLine` makes: a surface
 * that prints a figure the save does not hold is a promise the game cannot keep. It reads the
 * per-species map directly rather than the whole-save best, because the pole is about the fish
 * being drawn and a record belonging to some other animal underneath it would be a lie told
 * by adjacency.
 */
/**
 * FORMS LANDED OF ONE SPECIES, and why this counts a key it cannot parse.
 *
 * `state.variants` is keyed `<speciesId>:<variantId>`, unlike every other earned map, which
 * is keyed by species alone. A reader that only accepted the compound shape would report
 * nothing for a bare species key - and `earned-is-rendered.test.ts` probes every earned map
 * generically, with a bare key, precisely so that it does not have to know one field from
 * another. Making the fence field-aware would hand back the name-binding it was written to
 * remove, and that binding is what let the personal best ship unrendered.
 *
 * So this counts every entry belonging to the species and names the rarest form whose id
 * this build knows. An entry whose form half is missing or unknown still counts toward the
 * total, because anything in this map is by definition a form. Nothing malformed reaches a
 * real save: `normaliseTally` rejects unknown keys with `isKnownVariantKey` at load.
 */
export function formsNote(state: SimState, speciesId: string): string {
  let total = 0;
  let rarest: { id: string; rate: number } | null = null;
  for (const [key, n] of Object.entries(state.variants ?? {})) {
    if (!Number.isFinite(n) || n <= 0) continue;
    const cut = key.indexOf(':');
    const sp = cut < 0 ? key : key.slice(0, cut);
    if (sp !== speciesId) continue;
    total += n;
    const form = cut < 0 ? undefined : variantById(key.slice(cut + 1));
    if (form !== undefined && (rarest === null || form.per1000 < rarest.rate)) {
      rarest = { id: form.name.toLowerCase(), rate: form.per1000 };
    }
  }
  if (total <= 0) return '';
  if (rarest !== null) return `${countWords(total)} ${rarest.id}`;
  return `${countWords(total)} ${total === 1 ? 'form' : 'forms'}`;
}

export function recordNote(state: SimState, speciesId: string | null): string {
  if (speciesId === null) return '';
  const forms = formsNote(state, speciesId);
  const g = state.records[speciesId] ?? 0;
  const best = Number.isFinite(g) && g > 0 ? `best ${weightWords(g)}` : '';
  if (best === '' ) return forms;
  if (forms === '') return best;
  return `${best}, ${forms}`;
}

/**
 * WHAT THE SHEET SAYS WHEN A FISH COMES ABOARD, and why it says the weight.
 *
 * The interface's one piece of motion is the catch mark on the water, which shows WHICH
 * animal and nothing else. A player who has just landed the biggest fish of their save had no
 * way of knowing it, so the record was a field the engine kept and nobody was ever told about.
 * This is the moment it is worth telling: the note is transient like everything else the
 * second line says, and the standing figure stays on the pole afterwards.
 */
export function catchLine(
  speciesId: string,
  grams: number,
  isRecord: boolean,
  variantId?: string,
): string | null {
  const s = speciesById(speciesId);
  if (s === undefined) return null;
  // An Elder Mackerel is the thing a player tells somebody about. At 1 in 1,000 landings it
  // is rare enough that the sentence has to name it, and the species name alone would waste it.
  const form = variantId === undefined ? undefined : variantById(variantId);
  const plain = s.name.toLowerCase();
  const name = form === undefined ? plain : `${form.name.toLowerCase()} ${plain}`;
  const head = grams > 0 ? `${article(name)} ${name}, ${weightWords(grams)}.` : `${article(name)} ${name}.`;
  return isRecord ? `${head} Heaviest yet.` : head;
}
